import { useMutation } from '@apollo/client/react';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import { transformIntermodalBookingToUpdateFormState } from '../../../use-cases/intermodal/utils/detailsUtils';
import {
  addBookingResultNotification,
  type IntermodalResult,
} from '../../notifications/notificationsActions';
import { showBookingResultToast } from '../../notifications/utils/showToast';
import { cancelIntermodalBookingMutation } from '../graphql/cancelIntermodalBookingMutation';
import { setUpdateIntermodalBookingFormValue } from '../updateIntermodalReducer';

interface UseCancelBookingParams {
  bookingNo: number;
  onFail?: () => void;
  onSuccess?: () => void;
}

export const useCancelIntermodalBooking = ({
  bookingNo,
  onFail,
  onSuccess,
}: UseCancelBookingParams) => {
  const dispatch = useDispatch();

  const [submitting, setSubmitting] = useState(false);

  const [cancelIntermodalBooking] = useMutation(cancelIntermodalBookingMutation, {
    variables: { bookingNo },
  });

  const submit = useCallback(() => {
    setSubmitting(true);
    cancelIntermodalBooking()
      .then(result => {
        setSubmitting(false);

        if (result.data?.cancelIntermodalBooking) {
          if (result.data.cancelIntermodalBooking.success) {
            const cancelledIntermodalBooking = result.data.cancelIntermodalBooking.booking!;

            const bookingResult: IntermodalResult = {
              type: 'intermodal-cancel-success',
              bookingNo: cancelledIntermodalBooking.bookingNo,
              messages: result.data.cancelIntermodalBooking.messages.map(msg => msg.description),
              warnings: result.data.cancelIntermodalBooking.warnings.map(msg => msg.description),
            };

            dispatch(
              setUpdateIntermodalBookingFormValue(
                transformIntermodalBookingToUpdateFormState(cancelledIntermodalBooking),
              ),
            );

            showBookingResultToast(bookingResult);
            dispatch(addBookingResultNotification(bookingResult));

            onSuccess?.();
          } else {
            const bookingResult: IntermodalResult = {
              bookingNo,
              type: 'intermodal-cancel-fail',
              errors: result.data.cancelIntermodalBooking.errors.map(msg => msg.description),
            };

            showBookingResultToast(bookingResult);
            dispatch(addBookingResultNotification(bookingResult));

            onFail?.();
          }
        }
      })
      .catch(() => {
        setSubmitting(false);

        onFail?.();
      });
  }, [bookingNo, cancelIntermodalBooking, dispatch, onFail, onSuccess]);

  return {
    submit,
    submitting,
  };
};
